import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../../hooks/useCart';
import { useWishlist } from '../../../hooks/useWishlist';

const menuLinks = [
  {
    section: 'Shop',
    items: [
      { label: 'Home', path: '/', icon: 'ri-home-4-line' },
      { label: 'Shop All', path: '/shop', icon: 'ri-store-2-line' },
    ],
  },
  {
    section: 'Policies',
    items: [
      { label: 'Return & Refund Policy', path: '/refund', icon: 'ri-refund-2-line' },
      { label: 'Shipping Policy', path: '/shipping', icon: 'ri-truck-line' },
      { label: 'Terms Of Service', path: '/terms', icon: 'ri-file-text-line' },
      { label: 'Privacy Policy', path: '/privacy', icon: 'ri-shield-check-line' },
    ],
  },
  {
    section: 'Info',
    items: [
      { label: 'Contact Us', path: '/contact', icon: 'ri-mail-line' },
    ],
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pagesOpen, setPagesOpen] = useState(false);
  const { totalCount, openCart } = useCart();
  const { count: wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const isShopPage = location.pathname === '/shop' || location.pathname.startsWith('/product') || location.pathname === '/wishlist';
  const pagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pagesRef.current && !pagesRef.current.contains(e.target as Node)) {
        setPagesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close pages dropdown on route change
  useEffect(() => {
    setPagesOpen(false);
  }, [location.pathname]);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navItems = [
    { label: 'Our Story', id: 'our-story' },
    { label: 'Collection', id: 'collection' },
    { label: 'Universe', id: 'universe' },
    { label: 'Signature', id: 'signature' },
    { label: 'Community', id: 'community' },
  ];

  const navBg = scrolled || isShopPage
    ? 'bg-black/95 backdrop-blur-md border-b border-[#00d4ff]/10 shadow-[0_4px_30px_rgba(0,212,255,0.08)]'
    : 'bg-transparent';

  const handleNavLink = (path: string) => {
    setPagesOpen(false);
    navigate(path);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${navBg}`}>
        <div className="w-[90%] max-w-[1400px] mx-auto flex items-center justify-between h-16">
          {/* Logo */}
          <button
            onClick={() => { setMenuOpen(false); navigate('/'); }}
            className="flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <img
              src="https://static.readdy.ai/image/7d0ce02f18177c7f8cb3537c80e36c26/38e85e5700c485e1f17cbbb7a365341f.png"
              alt="Motion Boyz"
              className="h-8 w-auto object-contain"
            />
            <span
              className="text-white font-bold text-base tracking-widest uppercase hidden sm:block"
              style={{ textShadow: '0 0 12px rgba(0,212,255,0.5)' }}
            >
              Motion Boyz
            </span>
          </button>

          {/* Desktop Nav Links */}
          <ul className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => scrollTo(item.id)}
                  className="text-gray-400 hover:text-[#00d4ff] text-sm tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap relative group"
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#00d4ff] group-hover:w-full transition-all duration-300"></span>
                </button>
              </li>
            ))}
            <li>
              <button
                onClick={() => { setMenuOpen(false); navigate('/shop'); }}
                className="text-[#00d4ff] hover:text-white text-sm tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap relative group"
              >
                Shop All
                <span className="absolute -bottom-1 left-0 w-full h-px bg-[#00d4ff] group-hover:bg-white transition-all duration-300"></span>
              </button>
            </li>
          </ul>

          {/* CTA */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/shop')}
              className="hidden sm:block whitespace-nowrap px-4 py-2 text-xs tracking-widest uppercase font-semibold text-black bg-[#00d4ff] rounded-full hover:bg-white transition-colors duration-300 cursor-pointer"
              style={{ boxShadow: '0 0 16px rgba(0,212,255,0.4)' }}
            >
              Shop Now
            </button>

            {/* Wishlist */}
            <button
              onClick={() => navigate('/wishlist')}
              className="relative w-9 h-9 flex items-center justify-center rounded-full border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
              aria-label="Wishlist"
            >
              <i className="ri-heart-line text-lg"></i>
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none">
                  {wishlistCount > 99 ? '99+' : wishlistCount}
                </span>
              )}
            </button>

            {/* Cart */}
            <button
              onClick={openCart}
              className="relative w-9 h-9 flex items-center justify-center rounded-full border border-[#00d4ff]/40 text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
              aria-label="Cart"
            >
              <i className="ri-shopping-cart-line text-lg"></i>
              {totalCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-[#00d4ff] text-black text-[10px] font-bold leading-none">
                  {totalCount > 99 ? '99+' : totalCount}
                </span>
              )}
            </button>

            {/* Pages Menu */}
            <div className="relative" ref={pagesRef}>
              <button
                onClick={() => setPagesOpen((v) => !v)}
                className={`w-9 h-9 flex items-center justify-center rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  pagesOpen
                    ? 'border-[#00d4ff] bg-[#00d4ff]/15 text-[#00d4ff]'
                    : 'border-white/20 text-gray-300 hover:border-[#00d4ff] hover:text-[#00d4ff]'
                }`}
                aria-label="Pages Menu"
              >
                <i className={`text-lg transition-transform duration-300 ${pagesOpen ? 'ri-close-line' : 'ri-apps-2-line'}`}></i>
              </button>

              {/* Dropdown Panel */}
              <div
                className={`absolute right-0 top-12 w-64 bg-black/98 border border-white/10 rounded-lg overflow-hidden transition-all duration-300 origin-top-right ${
                  pagesOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                }`}
                style={{ backdropFilter: 'blur(20px)', boxShadow: '0 16px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,212,255,0.08)' }}
              >
                {menuLinks.map((group, gi) => (
                  <div key={group.section}>
                    {gi > 0 && <div className="mx-4 border-t border-white/5"></div>}
                    <div className="px-4 pt-3 pb-1">
                      <p className="text-[#00d4ff] text-[10px] tracking-widest uppercase font-semibold">{group.section}</p>
                    </div>
                    {group.items.map((link) => (
                      <button
                        key={link.path}
                        onClick={() => handleNavLink(link.path)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-200 cursor-pointer text-left group ${
                          location.pathname === link.path
                            ? 'text-[#00d4ff] bg-[#00d4ff]/8'
                            : 'text-gray-300 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <span className={`w-5 h-5 flex items-center justify-center text-base flex-shrink-0 ${
                          location.pathname === link.path ? 'text-[#00d4ff]' : 'text-gray-500 group-hover:text-[#00d4ff]'
                        } transition-colors duration-200`}>
                          <i className={link.icon}></i>
                        </span>
                        <span className="tracking-wide text-xs uppercase">{link.label}</span>
                        {location.pathname === link.path && (
                          <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#00d4ff] flex-shrink-0"></span>
                        )}
                      </button>
                    ))}
                    {gi === menuLinks.length - 1 && <div className="h-2"></div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Hamburger */}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-[#00d4ff] hover:text-[#00d4ff] transition-all duration-300 cursor-pointer"
              aria-label="Menu"
            >
              <i className={`text-lg ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-400 bg-black/98 border-t border-[#00d4ff]/10 ${
            menuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <ul className="flex flex-col px-6 py-4 gap-1">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => scrollTo(item.id)}
                  className="w-full text-left text-gray-300 hover:text-[#00d4ff] text-sm tracking-widest uppercase py-3 border-b border-white/5 transition-colors duration-300 cursor-pointer"
                >
                  {item.label}
                </button>
              </li>
            ))}
            <li>
              <button
                onClick={() => { setMenuOpen(false); navigate('/shop'); }}
                className="w-full text-left text-[#00d4ff] text-sm tracking-widest uppercase py-3 border-b border-white/5 transition-colors duration-300 cursor-pointer"
              >
                Shop All
              </button>
            </li>
            <li>
              <button
                onClick={() => { setMenuOpen(false); navigate('/wishlist'); }}
                className="w-full text-left text-rose-400 text-sm tracking-widest uppercase py-3 border-b border-white/5 transition-colors duration-300 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-heart-line"></i>Wishlist
                {wishlistCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-rose-500 text-white text-[10px] font-bold rounded-full leading-none">
                    {wishlistCount}
                  </span>
                )}
              </button>
            </li>
            {/* Mobile policy links */}
            <li className="pt-1">
              <p className="text-[#00d4ff] text-[10px] tracking-widest uppercase font-semibold py-2">Policies & Info</p>
            </li>
            {[
              { label: 'Return & Refund Policy', path: '/refund' },
              { label: 'Shipping Policy', path: '/shipping' },
              { label: 'Terms Of Service', path: '/terms' },
              { label: 'Privacy Policy', path: '/privacy' },
              { label: 'Contact Us', path: '/contact' },
            ].map((link) => (
              <li key={link.path}>
                <button
                  onClick={() => { setMenuOpen(false); navigate(link.path); }}
                  className="w-full text-left text-gray-400 hover:text-white text-xs tracking-widest uppercase py-2.5 border-b border-white/5 transition-colors duration-300 cursor-pointer"
                >
                  {link.label}
                </button>
              </li>
            ))}
            <li className="pt-3 pb-2">
              <button
                onClick={() => { setMenuOpen(false); navigate('/shop'); }}
                className="w-full py-3 bg-[#00d4ff] text-black text-xs tracking-widest uppercase font-semibold rounded-full cursor-pointer whitespace-nowrap"
                style={{ boxShadow: '0 0 16px rgba(0,212,255,0.4)' }}
              >
                Shop Now
              </button>
            </li>
          </ul>
        </div>
      </nav>
    </>
  );
}
